import * as THREE from "three";
import { Monk } from "../entities/Monk";
import { Portal } from "./Portal";
// import { SoundManager } from "./SoundManager.js";

type Difficulty = "easy" | "medium" | "hard";
const MONK_COLORS = ["red", "blue", "black"];
const GRID_SIZE = 18; // Smaller game grid

// AI configuration
const AI_MOVE_INTERVALS = {
  easy: 200, // 0.2 seconds
  medium: 400, // 0.4 seconds
  hard: 800, // 0.8 seconds
};

const AI_WRONG_MOVE_CHANCES = {
  easy: 0.3, // 30% chance
  medium: 0.2, // 20% chance
  hard: 0.1, // 10% chance
};

export class Game {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private monks: Monk[] = [];
  private player1!: Monk;
  private player2!: Monk;
  private npcs: Monk[] = [];
  private lastTime: number = 0;
  private lastAIMove: number = 0;
  private keys: Set<string> = new Set();
  private gameMode: "single" | "double" | null = null;
  private difficulty: Difficulty = "medium";
  private currentRound: number = 1;
  private scores: { player1: number; player2: number } = {
    player1: 0,
    player2: 0,
  };
  private hitSound: HTMLAudioElement;
  private backgroundMusic: HTMLAudioElement;
  private smokeParticles: THREE.Points[] = [];
  private isMobile: boolean = false;
  private virtualControls: { [key: string]: boolean } = {
    up: false,
    down: false,
    left: false,
    right: false,
    strike: false,
  };
  private virtualControlsP2: { [key: string]: boolean } = {
    up: false,
    down: false,
    left: false,
    right: false,
    strike: false,
  };
  private isGameLoopRunning: boolean = false;
  private isRoundEnding: boolean = false;
  //   private soundManager: SoundManager;
  private startPortal: Portal | null = null;
  private exitPortal: Portal | null = null;
  private isPortalEnabled: boolean = false;

  constructor() {
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.renderer = new THREE.WebGLRenderer();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    document.body.appendChild(this.renderer.domElement);

    // Check if mobile
    this.isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    // Setup lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight.position.set(5, 5, 5);
    this.scene.add(directionalLight);

    // Setup camera with tilted view - position further back to see the entire grid
    this.camera.position.set(0, 12, 12); // Reduced height and distance for closer view
    this.camera.lookAt(0, 0, 0);

    // Setup ground with visible boundaries
    const groundGeometry = new THREE.PlaneGeometry(GRID_SIZE, GRID_SIZE);
    const groundMaterial = new THREE.MeshPhongMaterial({ color: 0x808080 });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.5;
    this.scene.add(ground);

    // Add a visible boundary to help players see the game limits
    const boundaryGeometry = new THREE.EdgesGeometry(
      new THREE.BoxGeometry(GRID_SIZE, 1, GRID_SIZE)
    );
    const boundaryMaterial = new THREE.LineBasicMaterial({
      color: 0xffffff,
      opacity: 0.5,
      transparent: true,
    });
    const boundary = new THREE.LineSegments(boundaryGeometry, boundaryMaterial);
    boundary.position.y = -0.5;
    this.scene.add(boundary);

    // Add corner markers for better visibility
    this.addCornerMarkers();

    // Create portals
    this.startPortal = new Portal(
      this.scene,
      new THREE.Vector3(-GRID_SIZE / 2 - 2, 2, -GRID_SIZE / 2 + 4),
      true
    );
    this.exitPortal = new Portal(
      this.scene,
      new THREE.Vector3(GRID_SIZE / 2 + 2, 2, -GRID_SIZE / 2 + 4)
    );
    this.isPortalEnabled = true;

    // Setup audio
    this.hitSound = new Audio("/assets/sounds/hit.mp3");
    this.hitSound.volume = 0.3;
    this.backgroundMusic = new Audio("/assets/sounds/background.mp3");
    this.backgroundMusic.loop = true;
    this.backgroundMusic.volume = 0.5;
    // this.soundManager = new SoundManager();

    // Setup event listeners
    window.addEventListener("keydown", this.handleKeyDown.bind(this));
    window.addEventListener("keyup", this.handleKeyUp.bind(this));
    window.addEventListener("resize", this.handleResize.bind(this));

    // Setup menu buttons
    const singlePlayerBtn = document.getElementById(
      "singlePlayer"
    ) as HTMLButtonElement;
    const twoPlayerBtn = document.getElementById(
      "twoPlayer"
    ) as HTMLButtonElement;

    // Start background music on first button click
    const startMusic = () => {
      this.backgroundMusic.play().catch(() => {
        console.log("Music autoplay blocked by browser");
      });
      if (singlePlayerBtn)
        singlePlayerBtn.removeEventListener("click", startMusic);
      if (twoPlayerBtn) twoPlayerBtn.removeEventListener("click", startMusic);
    };

    if (singlePlayerBtn) {
      singlePlayerBtn.addEventListener("click", startMusic);
      singlePlayerBtn.onclick = () => this.showDifficultyMenu();
    }
    if (twoPlayerBtn) {
      twoPlayerBtn.addEventListener("click", startMusic);
      // Enable 2-player mode for mobile devices as well
      twoPlayerBtn.onclick = () => this.startGame("double");
      // Remove disabled state for mobile
      twoPlayerBtn.disabled = false;
      twoPlayerBtn.style.opacity = "1";
      twoPlayerBtn.style.cursor = "pointer";
    }

    // Try to start music immediately (might be blocked by browser)
    this.backgroundMusic.play().catch(() => {
      console.log(
        "Music autoplay blocked by browser, will start on user interaction"
      );
    });

    // Hide virtual controls initially
    const virtualControls = document.getElementById("virtual-controls");
    const virtualControlsP2 = document.getElementById("virtual-controls-p2");
    if (virtualControls) {
      virtualControls.style.display = "none";
    }
    if (virtualControlsP2) {
      virtualControlsP2.style.display = "none";
    }

    // Hide score initially
    const scoreElement = document.getElementById("score");
    if (scoreElement) {
      scoreElement.style.display = "none";
    }

    // Start render loop for portals only
    this.renderLoop();

    // Add click handlers for portals in main menu
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    const handleClick = (event: MouseEvent) => {
      if (!this.gameMode) {
        // Only handle clicks in main menu
        mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
        raycaster.setFromCamera(mouse, this.camera);

        const intersects = raycaster.intersectObjects(this.scene.children);
        for (const intersect of intersects) {
          if (intersect.object === this.startPortal?.getPortalGroup()) {
            const urlParams = new URLSearchParams(window.location.search);
            const refUrl = urlParams.get("ref");
            if (refUrl) {
              let url = refUrl;
              if (!url.startsWith("http://") && !url.startsWith("https://")) {
                url = "https://" + url;
              }
              window.location.href = url;
            }
            break;
          } else if (intersect.object === this.exitPortal?.getPortalGroup()) {
            const params = new URLSearchParams();
            params.append("portal", "true");
            params.append("username", "Player1");
            params.append("color", "white");
            params.append("speed", "5");
            params.append("ref", window.location.href);
            window.location.href =
              "https://portal.pieter.com?" + params.toString();
            break;
          }
        }
      }
    };

    window.addEventListener("click", handleClick);
  }

  private renderLoop = () => {
    // Only render the scene
    this.renderer.render(this.scene, this.camera);
    requestAnimationFrame(this.renderLoop);
  };

  private setupVirtualControls(): void {
    const controls = document.getElementById("virtual-controls")!;
    const controlsP2 = document.getElementById("virtual-controls-p2")!;

    // Position controls for one player (left corner) or two player mode
    if (this.gameMode === "single") {
      controls.classList.add("left");
      controls.style.display = "grid";
      controlsP2.style.display = "none";
    } else if (this.gameMode === "double") {
      controls.classList.add("left");
      controls.style.display = "grid";
      controlsP2.style.display = "grid";
    }

    // Add player-specific styles
    const dpad = controls.querySelector(".d-pad")! as HTMLElement;
    const strikeBtn = document.getElementById("v-strike")!;
    dpad.classList.add("player1");
    strikeBtn.classList.add("player1");

    // Setup player 1 controls
    const p1Buttons = [
      { id: "v-up", control: "up" },
      { id: "v-left", control: "left" },
      { id: "v-right", control: "right" },
      { id: "v-down", control: "down" },
      { id: "v-strike", control: "strike" },
    ];

    p1Buttons.forEach(({ id, control }) => {
      const button = document.getElementById(id)!;
      button.addEventListener("touchstart", (e) => {
        e.preventDefault();
        this.virtualControls[control] = true;
      });
      button.addEventListener("touchend", (e) => {
        e.preventDefault();
        this.virtualControls[control] = false;
      });
    });

    // Setup player 2 controls if in two-player mode
    if (this.gameMode === "double") {
      const p2Buttons = [
        { id: "v-up-p2", control: "up" },
        { id: "v-left-p2", control: "left" },
        { id: "v-right-p2", control: "right" },
        { id: "v-down-p2", control: "down" },
        { id: "v-strike-p2", control: "strike" },
      ];

      p2Buttons.forEach(({ id, control }) => {
        const button = document.getElementById(id)!;
        button.addEventListener("touchstart", (e) => {
          e.preventDefault();
          this.virtualControlsP2[control] = true;
        });
        button.addEventListener("touchend", (e) => {
          e.preventDefault();
          this.virtualControlsP2[control] = false;
        });
      });
    }
  }

  private showDifficultyMenu(): void {
    const menuElement = document.getElementById("menu");
    if (menuElement) {
      menuElement.innerHTML = `
        <h1>Select Difficulty</h1>
        <p>Choose how smart the AI opponent should be</p>
        <button id="easy">Easy</button>
        <button id="medium">Medium</button>
        <button id="hard">Hard</button>
      `;

      const easyBtn = document.getElementById("easy");
      const mediumBtn = document.getElementById("medium");
      const hardBtn = document.getElementById("hard");

      if (easyBtn) {
        easyBtn.onclick = () => {
          this.difficulty = "easy";
          this.startGame("single");
        };
      }
      if (mediumBtn) {
        mediumBtn.onclick = () => {
          this.difficulty = "medium";
          this.startGame("single");
        };
      }
      if (hardBtn) {
        hardBtn.onclick = () => {
          this.difficulty = "hard";
          this.startGame("single");
        };
      }
    }
  }

  // private createSmokeEffect(position: THREE.Vector3): void {
  //   const particleCount = 20;
  //   const geometry = new THREE.BufferGeometry();
  //   const positions = new Float32Array(particleCount * 3);
  //   const velocities = new Float32Array(particleCount * 3);

  //   for (let i = 0; i < particleCount; i++) {
  //     positions[i * 3] = position.x;
  //     positions[i * 3 + 1] = position.y + 1;
  //     positions[i * 3 + 2] = position.z;

  //     velocities[i * 3] = (Math.random() - 0.5) * 0.1;
  //     velocities[i * 3 + 1] = Math.random() * 0.1;
  //     velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.1;
  //   }

  //   geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  //   const material = new THREE.PointsMaterial({
  //     color: 0x888888,
  //     size: 0.1,
  //     transparent: true,
  //     opacity: 0.6,
  //   });

  //   const points = new THREE.Points(geometry, material);
  //   this.scene.add(points);
  //   this.smokeParticles.push(points);

  //   // Call the createHitEffect for compatibility
  //   this.createHitEffect(position);
  // }

  private updateSmokeParticles(): void {
    for (let i = this.smokeParticles.length - 1; i >= 0; i--) {
      const points = this.smokeParticles[i];
      const positions = points.geometry.attributes.position
        .array as Float32Array;

      let shouldRemove = true;

      for (let j = 0; j < positions.length; j += 3) {
        // Add some random movement
        positions[j] += (Math.random() - 0.5) * 0.05;
        positions[j + 1] += 0.05 + Math.random() * 0.05; // Always rise
        positions[j + 2] += (Math.random() - 0.5) * 0.05;

        // If any particle is still visible, don't remove the system yet
        if (positions[j + 1] < 5) {
          shouldRemove = false;
        }
      }

      points.geometry.attributes.position.needsUpdate = true;

      // Fade out
      if (points.material instanceof THREE.PointsMaterial) {
        points.material.opacity -= 0.01;
        if (points.material.opacity <= 0) {
          shouldRemove = true;
        }
      }

      // Remove particle system if all particles are out of view
      if (shouldRemove) {
        this.scene.remove(points);
        this.smokeParticles.splice(i, 1);
      }
    }
  }

  private getRandomMonkColor(): string {
    return MONK_COLORS[Math.floor(Math.random() * MONK_COLORS.length)];
  }

  private getRandomPosition(): THREE.Vector3 {
    // Use a smaller area than the full grid size to ensure monks are visible
    // Keep a 10% margin from the edges
    const margin = GRID_SIZE * 0.1;
    const safeHalfSize = GRID_SIZE / 2 - margin;

    return new THREE.Vector3(
      Math.random() * (safeHalfSize * 2) - safeHalfSize,
      0,
      Math.random() * (safeHalfSize * 2) - safeHalfSize
    );
  }

  private async startGame(mode: "single" | "double"): Promise<void> {
    console.log("Starting game in mode:", mode);
    this.gameMode = mode;
    this.scores = { player1: 0, player2: 0 };
    this.currentRound = 1;

    // Show initial game hint
    const controls = this.isMobile
      ? mode === "single"
        ? "Use the virtual controls on the left to move and tap ⚔ to strike!"
        : "Player 1: Use BLUE controls on the left\nPlayer 2: Use ORANGE controls on the right"
      : mode === "single"
      ? "Use WASD to move and F to strike!"
      : "Player 1: WASD to move, F to strike\nPlayer 2: Arrow keys to move, Enter to strike";

    const menuElement = document.getElementById("menu");
    if (menuElement) {
      menuElement.style.display = "none";
    }

    // Show score
    const scoreElement = document.getElementById("score");
    if (scoreElement) {
      scoreElement.style.display = "block";
      scoreElement.textContent = "Player 1: 0 | Player 2: 0";
    }

    // Setup virtual controls for mobile
    if (this.isMobile) {
      this.setupVirtualControls();
    }

    try {
      console.log("Showing welcome popup");
      await this.showPopup(
        `Welcome to Monk Vs Monk!\n\n${controls}\n\nFind and strike your opponent while staying hidden among the crowd. But be careful - your movements might give you away!`,
        "Start Game"
      );
      console.log("Welcome popup closed");

      this.backgroundMusic.play();
      console.log("Starting first round");
      await this.startRound();
    } catch (error) {
      console.error("Error in startGame:", error);
    }
  }

  private async startRound(): Promise<void> {
    console.log("Starting round", this.currentRound);

    // Clear existing monks and smoke
    this.monks.forEach((monk) => this.scene.remove(monk.mesh));
    this.smokeParticles.forEach((particles) => this.scene.remove(particles));
    this.monks = [];
    this.npcs = [];
    this.smokeParticles = [];

    try {
      // Show round description
      console.log("Showing round description");
      await this.showGameDescription();
      console.log("Round description closed");

      // Create players with random colors
      this.player1 = new Monk(
        this.getRandomMonkColor(),
        this.getRandomPosition()
      );
      this.player2 = new Monk(
        this.getRandomMonkColor(),
        this.getRandomPosition()
      );

      // Mark players as player monks to allow out-of-bounds movement
      this.player1.mesh.userData.isPlayer = true;
      this.player2.mesh.userData.isPlayer = true;

      this.monks = [this.player1, this.player2];

      // Create NPCs with random colors
      for (let i = 0; i < 65; i++) {
        const npc = new Monk(
          this.getRandomMonkColor(),
          this.getRandomPosition()
        );
        // Explicitly mark NPCs as non-players
        npc.mesh.userData.isPlayer = false;
        this.npcs.push(npc);
        this.monks.push(npc);
      }

      // Add all monks to scene and ensure they have proper rotation
      this.monks.forEach((monk) => {
        // Ensure no rotation is carried over from previous rounds
        monk.mesh.rotation.set(0, 0, 0);
        this.scene.add(monk.mesh);
      });

      // Reset timers and state
      this.lastTime = performance.now();
      this.lastAIMove = performance.now();
      this.isRoundEnding = false;

      // Start game loop if not already running
      if (!this.isGameLoopRunning) {
        console.log("Starting game loop");
        this.isGameLoopRunning = true;
        this.gameLoop();
      }
    } catch (error) {
      console.error("Error in startRound:", error);
    }
  }

  private showGameDescription(): Promise<void> {
    // Just show "Round X" without additional text and no button
    return this.showPopup(`Round ${this.currentRound}`, "");
  }

  private showPopup(message: string, buttonText: string = "OK"): Promise<void> {
    return new Promise((resolve) => {
      console.log("Creating popup with message:", message);

      // Remove any existing popups first
      const existingPopups = document.querySelectorAll(".popup");
      existingPopups.forEach((popup) => popup.remove());

      const popup = document.createElement("div");
      popup.className = "popup";
      popup.style.display = "flex";
      popup.innerHTML = `
        <div class="popup-content">
          <p>${message.split("\n").join("<br>")}</p>
          ${buttonText ? `<button>${buttonText}</button>` : ""}
        </div>
      `;

      const button = popup.querySelector("button");

      if (button) {
        const handleClick = () => {
          console.log("Popup button clicked");
          document.removeEventListener("keydown", handleKeyPress);
          popup.remove();
          resolve();
        };

        const handleKeyPress = (e: KeyboardEvent) => {
          if (e.key === "Enter") {
            console.log("Enter key pressed");
            handleClick();
          }
        };

        button.onclick = handleClick;
        document.addEventListener("keydown", handleKeyPress);
      } else {
        // Auto-close after 0.6 seconds if no button
        setTimeout(() => {
          popup.remove();
          resolve();
        }, 600);
      }

      document.body.appendChild(popup);
      console.log("Popup added to document");
    });
  }

  private updateAI(): void {
    if (this.gameMode !== "single") return;

    // Don't update AI if player2 (AI) is dead
    if (this.player2.isDead()) return;

    const currentTime = performance.now();
    const interval = AI_MOVE_INTERVALS[this.difficulty];
    if (currentTime - this.lastAIMove < interval) {
      return;
    }

    const moveSpeed = 0.1;
    const direction = new THREE.Vector3();
    const target = this.player1.mesh.position;
    const current = this.player2.mesh.position;
    const distance = current.distanceTo(target);

    // Calculate direction to player
    direction.subVectors(target, current).normalize();

    // Determine best move based on direction
    const moves = [
      { dir: new THREE.Vector3(0, 0, -1), key: "up" },
      { dir: new THREE.Vector3(0, 0, 1), key: "down" },
      { dir: new THREE.Vector3(-1, 0, 0), key: "left" },
      { dir: new THREE.Vector3(1, 0, 0), key: "right" },
    ];

    let bestMove = moves[0];
    let bestScore = -Infinity;

    moves.forEach((move) => {
      const score = move.dir.dot(direction);
      if (score > bestScore) {
        bestScore = score;
        bestMove = move;
      }
    });

    // Chance to make wrong move based on difficulty
    const wrongMoveChance = AI_WRONG_MOVE_CHANCES[this.difficulty];
    if (Math.random() < wrongMoveChance) {
      // Pick a random move that isn't the best move
      const wrongMoves = moves.filter((m) => m !== bestMove);
      bestMove = wrongMoves[Math.floor(Math.random() * wrongMoves.length)];
    }

    // Execute move
    if (distance < 1.5) {
      // Close enough to strike
      if (!this.player2.mesh.userData.preparingStrike) {
        // Set a flag that the AI is preparing to strike
        this.player2.mesh.userData.preparingStrike = true;

        // Simply wait 1 second before striking
        setTimeout(() => {
          if (this.player2 && !this.player2.isDead() && !this.isRoundEnding) {
            // Only strike if the player is still close
            const newDistance = this.player2.mesh.position.distanceTo(
              this.player1.mesh.position
            );
            if (newDistance < 1.5) {
              this.player2.strike();
            }
            // Reset the flag
            this.player2.mesh.userData.preparingStrike = false;
          }
        }, 1000);
      }
    } else {
      // Move in the chosen direction
      this.player2.move(bestMove.dir.multiplyScalar(moveSpeed));
    }

    this.lastAIMove = currentTime;
  }

  private updateNPCs(): void {
    this.npcs.forEach((npc) => {
      // Skip dead NPCs
      if (npc.isDead()) return;

      // NPCs should only move in cardinal directions, no rotation or striking
      if (Math.random() < 0.01) {
        // Less frequent but bigger movements in one of four directions
        const directions = [
          new THREE.Vector3(0, 0, -1), // up
          new THREE.Vector3(0, 0, 1), // down
          new THREE.Vector3(-1, 0, 0), // left
          new THREE.Vector3(1, 0, 0), // right
        ];

        // Pick a random cardinal direction
        const randomDirection =
          directions[Math.floor(Math.random() * directions.length)];
        const moveSpeed = 0.2; // Doubled movement speed

        // Apply movement
        npc.move(randomDirection.clone().multiplyScalar(moveSpeed));
      }
    });
  }

  private updatePlayerMovement(): void {
    const moveSpeed = 0.1;
    const direction = new THREE.Vector3();

    // Player 1 (WASD or virtual controls)
    if (this.isMobile) {
      if (this.virtualControls.up) direction.z -= moveSpeed;
      if (this.virtualControls.down) direction.z += moveSpeed;
      if (this.virtualControls.left) direction.x -= moveSpeed;
      if (this.virtualControls.right) direction.x += moveSpeed;
      if (this.virtualControls.strike) {
        this.player1.strike();
        this.hitSound.currentTime = 0;
        this.hitSound.play();
      }
    } else {
      // Desktop
      if (this.keys.has("w")) direction.z -= moveSpeed;
      if (this.keys.has("s")) direction.z += moveSpeed;
      if (this.keys.has("a")) direction.x -= moveSpeed;
      if (this.keys.has("d")) direction.x += moveSpeed;
      if (this.keys.has("f")) {
        this.player1.strike();
        this.hitSound.currentTime = 0;
        this.hitSound.play();
      }
    }
    this.player1.move(direction);

    // Player 2 (arrow keys + Enter) if in 2-player mode
    if (this.gameMode === "double") {
      direction.set(0, 0, 0);

      if (this.isMobile) {
        // Virtual controls for player 2
        if (this.virtualControlsP2.up) direction.z -= moveSpeed;
        if (this.virtualControlsP2.down) direction.z += moveSpeed;
        if (this.virtualControlsP2.left) direction.x -= moveSpeed;
        if (this.virtualControlsP2.right) direction.x += moveSpeed;
        if (this.virtualControlsP2.strike) {
          this.player2.strike();
          this.hitSound.currentTime = 0;
          this.hitSound.play();
        }
      } else {
        // Desktop controls for player 2
        if (this.keys.has("arrowup")) direction.z -= moveSpeed;
        if (this.keys.has("arrowdown")) direction.z += moveSpeed;
        if (this.keys.has("arrowleft")) direction.x -= moveSpeed;
        if (this.keys.has("arrowright")) direction.x += moveSpeed;
        if (this.keys.has("enter")) {
          this.player2.strike();
          this.hitSound.currentTime = 0;
          this.hitSound.play();
        }
      }
      this.player2.move(direction);
    }
  }

  private async handleRoundEnd(winner: "player1" | "player2"): Promise<void> {
    if (this.isRoundEnding) return; // prevent multiple triggers
    this.isRoundEnding = true;

    // End round immediately
    const message =
      winner === "player1"
        ? "Player 1 wins the round!"
        : "Player 2 wins the round!";
    await this.showPopup(message);

    // Check if someone reached 3 points
    if (this.scores[winner] >= 3) {
      const finalMessage = `${
        winner === "player1" ? "Player 1" : "Player 2"
      } wins the game!`;
      await this.showPopup(finalMessage, "Play Again");
      // Reload to reset the entire game
      location.reload();
    } else {
      // Otherwise go to next round
      this.currentRound++;
      await this.startRound();
    }
  }

  private checkCollisions(): void {
    if (this.isRoundEnding) return;

    const checkStrike = (striker: Monk, target: Monk, isPlayer1: boolean) => {
      if (striker.isStriking && !target.isDead()) {
        const distance = striker.mesh.position.distanceTo(target.mesh.position);
        if (distance < 1.5) {
          this.hitSound.play();

          // Create hit effect at target position
          this.createHitEffect(target.mesh.position);

          // Mark target as dying but don't end round immediately
          target.die();

          // Update score
          if (isPlayer1) {
            this.scores.player1++;
          } else {
            this.scores.player2++;
          }
          this.updateScore();

          // Prevent immediate repeated hits
          striker.isStriking = false;

          // Set a small delay before ending the round
          setTimeout(() => {
            // Only end the round if we haven't already started ending it
            if (!this.isRoundEnding) {
              this.handleRoundEnd(isPlayer1 ? "player1" : "player2");
            }
          }, 1500); // 1.5 second delay
        }
      }
    };

    // Check player1 => player2
    if (this.player1.isStriking) {
      checkStrike(this.player1, this.player2, true);
    }
    // Check player2 => player1
    if (this.player2.isStriking) {
      checkStrike(this.player2, this.player1, false);
    }
  }

  private createHitEffect(position: THREE.Vector3): void {
    // Create a burst of particles
    const particleCount = 30;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = position.x;
      positions[i * 3 + 1] = position.y + 1.5; // Above the monk's head
      positions[i * 3 + 2] = position.z;

      // Yellow-red particles for hit effect
      colors[i * 3] = 1; // Full red
      colors[i * 3 + 1] = Math.random() * 0.5; // Some green
      colors[i * 3 + 2] = 0; // No blue
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.2,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
    });

    const points = new THREE.Points(geometry, material);
    this.scene.add(points);

    // Add velocity for each particle
    // const particleSystem = {
    //   points,
    //   velocities: Array(particleCount)
    //     .fill(0)
    //     .map(
    //       () =>
    //         new THREE.Vector3(
    //           (Math.random() - 0.5) * 3,
    //           Math.random() * 3 + 2,
    //           (Math.random() - 0.5) * 3
    //         )
    //     ),
    //   gravity: 0.1,
    //   lifetime: 0,
    // };

    // Add to smoke particles (will be animated in updateSmokeParticles)
    this.smokeParticles.push(points);

    // Create a "hit" text that floats up
    const hitCanvas = document.createElement("canvas");
    hitCanvas.width = 128;
    hitCanvas.height = 64;
    const ctx = hitCanvas.getContext("2d");
    if (ctx) {
      ctx.font = "48px Arial";
      ctx.fillStyle = "red";
      ctx.strokeStyle = "white";
      ctx.lineWidth = 2;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.strokeText("HIT!", 64, 32);
      ctx.fillText("HIT!", 64, 32);
    }

    const texture = new THREE.CanvasTexture(hitCanvas);
    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
    });
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.position.copy(position);
    sprite.position.y += 2;
    sprite.scale.set(2, 1, 1);
    this.scene.add(sprite);

    // Animate the hit text
    // const startY = sprite.position.y;
    const animate = () => {
      sprite.position.y += 0.03;
      sprite.material.opacity -= 0.02;

      if (sprite.material.opacity > 0) {
        requestAnimationFrame(animate);
      } else {
        this.scene.remove(sprite);
      }
    };

    animate();
  }

  private updateScore(): void {
    const scoreElement = document.getElementById("score");
    if (scoreElement) {
      scoreElement.textContent = `Player 1: ${this.scores.player1} | Player 2: ${this.scores.player2}`;
    }
  }

  private gameLoop(): void {
    if (!this.isGameLoopRunning) return;

    const currentTime = performance.now();
    const deltaTime = (currentTime - this.lastTime) / 1000;
    this.lastTime = currentTime;

    // Update game state
    if (!this.isRoundEnding && this.gameMode) {
      this.updatePlayerMovement();
      if (this.gameMode === "single") {
        this.updateAI();
      }
      this.updateNPCs();
      this.monks.forEach((monk) => monk.update(deltaTime));
      this.checkCollisions();
      this.updateSmokeParticles();
    }

    // Check portal collisions if game is active
    if (this.isPortalEnabled) {
      const checkPortalCollision = (player: Monk) => {
        if (player.isDead()) return; // Don't check portal collisions for dead monks

        const playerBox = new THREE.Box3().setFromObject(player.mesh);
        const portalRadius = 1.5; // Increased collision radius

        // Check start portal collision
        if (this.startPortal) {
          const portalPos = this.startPortal.getPosition();
          const distance = playerBox
            .getCenter(new THREE.Vector3())
            .distanceTo(portalPos);
          if (distance < portalRadius) {
            const urlParams = new URLSearchParams(window.location.search);
            const refUrl = urlParams.get("ref");
            if (refUrl) {
              let url = refUrl;
              if (!url.startsWith("http://") && !url.startsWith("https://")) {
                url = "https://" + url;
              }
              window.location.href = url;
            }
          }
        }

        // Check exit portal collision
        if (this.exitPortal) {
          const portalPos = this.exitPortal.getPosition();
          const distance = playerBox
            .getCenter(new THREE.Vector3())
            .distanceTo(portalPos);
          if (distance < portalRadius) {
            const params = new URLSearchParams();
            params.append("portal", "true");
            params.append("username", "Player1");
            params.append("color", "white");
            params.append("speed", "5");
            params.append("ref", window.location.href);
            window.location.href =
              "https://portal.pieter.com?" + params.toString();
          }
        }
      };

      // Check portal collisions for both players
      if (this.player1) {
        checkPortalCollision(this.player1);
      }
      if (this.player2) {
        checkPortalCollision(this.player2);
      }
    }

    requestAnimationFrame(this.gameLoop.bind(this));
  }

  private handleKeyDown(event: KeyboardEvent): void {
    this.keys.add(event.key.toLowerCase());
  }

  private handleKeyUp(event: KeyboardEvent): void {
    this.keys.delete(event.key.toLowerCase());
  }

  private handleResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);

    // Adjust camera position based on aspect ratio
    // For wider screens (landscape), move camera back to see more of the playing field
    if (window.innerWidth < window.innerHeight) {
      // Portrait mode (shouldn't happen with orientation lock, but just in case)
      this.camera.position.set(0, 18, 14);
    } else if (window.innerWidth / window.innerHeight > 1.7) {
      // Very wide screens
      this.camera.position.set(0, 15, 11);
    } else {
      // Standard landscape
      this.camera.position.set(0, 16, 12);
    }

    this.camera.lookAt(0, 0, 0);
  }

  private addCornerMarkers(): void {
    const halfSize = GRID_SIZE / 2;
    const markerSize = 0.5;
    const markerHeight = 1.5;

    // Create a marker mesh that will be reused
    const markerGeometry = new THREE.BoxGeometry(
      markerSize,
      markerHeight,
      markerSize
    );
    const markerMaterial = new THREE.MeshPhongMaterial({ color: 0xff5555 });

    // Create and position markers at each corner
    const cornerPositions = [
      new THREE.Vector3(-halfSize, 0, -halfSize),
      new THREE.Vector3(halfSize, 0, -halfSize),
      new THREE.Vector3(-halfSize, 0, halfSize),
      new THREE.Vector3(halfSize, 0, halfSize),
    ];

    cornerPositions.forEach((position) => {
      const marker = new THREE.Mesh(markerGeometry, markerMaterial);
      marker.position.copy(position);
      marker.position.y = markerHeight / 2 - 0.5; // Adjust to sit on the ground
      this.scene.add(marker);
    });
  }
}
